import { TenantConfigPlane } from '@today/types'
import { ProvisioningStatusPanelView } from '@today/ui-domain'
import { useTenantProvisioning } from '@/hooks/useTenantProvisioning'
import type { TenantConfigSectionProps } from '../section-registry'

/**
 * The console's `seeding` section (TO-375 REQ-013). Provisioning status is a
 * Super-Admin surface: on the tenant plane the section shows a notice and
 * sends no provisioning request.
 */
export function SeedingSection({ tenantId, plane, readOnly }: TenantConfigSectionProps) {
	if (plane !== TenantConfigPlane.PLATFORM) {
		return <p className="text-sm text-ds-text-muted">Provisioning status is managed by the platform team</p>
	}
	return <PlatformSeedingStatus tenantId={tenantId} canRetry={!readOnly} />
}

function PlatformSeedingStatus({ tenantId, canRetry }: { tenantId: string; canRetry: boolean }) {
	const { status, loadFailed, reload, retry, retrying, retryFailed } = useTenantProvisioning(tenantId)

	if (loadFailed) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 text-sm text-ds-danger">
				<p>Could not load the provisioning status.</p>
				<button type="button" onClick={reload} className="rounded-md border px-3 py-1">
					Try again
				</button>
			</div>
		)
	}
	if (!status) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading provisioning status…
			</p>
		)
	}
	return (
		<ProvisioningStatusPanelView
			rows={status.contexts}
			canRetry={canRetry}
			retrying={retrying}
			onRetry={retry}
			error={retryFailed ? 'The retry could not be sent — try again.' : null}
		/>
	)
}
